// ulearn student auth — Firebase PHONE sign-in + the uapply-backend
// ulearn-students service (/v1/ulearn-students).
//
// The student proves ownership of their number to Firebase over SMS; Firebase
// issues an ID token; this module ferries that token to the backend, which
// verifies it SERVER-SIDE and reads the phone number out of the verified
// payload. The number this bundle holds is never itself an authority — the
// backend ignores anything the client claims about identity.
//
// No secrets live here. The Firebase web config in src/firebase.js is public
// by design (it identifies the project; it does not authorise anything).
import { ULEARN_STUDENTS } from "../API";
import { addSessionEvent } from "../utils/sessionLog";

const TOKEN_KEY = "ulearnStudentToken";
const STUDENT_KEY = "ulearnStudent";
const GSI_SRC = "https://accounts.google.com/gsi/client";

export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

/* ------------------------------------------------------------ storage ---- */

export const getStudentToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const getStudent = () => {
  try {
    return JSON.parse(localStorage.getItem(STUDENT_KEY) || "null");
  } catch {
    return null;
  }
};

export const isSignedIn = () => Boolean(getStudentToken());

/**
 * Does this student still owe us an SMS verification before a test result can
 * be submitted?
 *
 * The end-of-test gate asks this. It is deliberately about the SESSION, not
 * about whatever number is currently typed into the form: the result attaches
 * to the signed-in account, so what has to be proven is that the account was
 * reached by someone holding that phone. A student who signed in earlier has
 * already proven it and is not asked twice.
 *
 * A session with no phone, or one the backend has not marked verified, is
 * treated as unverified — so a stale or hand-edited localStorage entry cannot
 * be used to walk past the gate.
 */
export const needsPhoneVerification = () => {
  if (!isSignedIn()) return true;
  const student = getStudent();
  return !(student && student.phone && student.phoneVerified !== false);
};

/** Authorization header when signed in, {} otherwise — spread into fetch
 *  headers so anonymous flows are byte-identical to before. */
export const authHeaders = () => {
  const token = getStudentToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emitAuthChanged = () => {
  try {
    window.dispatchEvent(new Event("ulearn-auth-changed"));
  } catch {
    /* older browsers: dropdowns refresh on next mount */
  }
};

const storeSession = (token, student) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(STUDENT_KEY, JSON.stringify(student));
  emitAuthChanged();
};

export const signOutStudent = () => {
  const wasSignedIn = isSignedIn();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(STUDENT_KEY);
  if (wasSignedIn) addSessionEvent("signed_out");

  // Firebase keeps its own session in IndexedDB. Leaving it behind would mean
  // a "signed out" student still holds a live Firebase credential — and the
  // next sign-in would silently reuse it instead of asking for a fresh OTP.
  // Imported dynamically so the auth SDK stays off the critical path for the
  // many pages that only need getStudent()/isSignedIn().
  import("../firebase")
    .then(({ auth }) => auth && auth.signOut())
    .catch(() => {
      /* Firebase unavailable (offline/blocked) — the ulearn session is already
         cleared, which is what governs this app's UI */
    });

  try {
    // legacy: stop Google auto-selecting this account if the old button is
    // ever re-enabled (see ULEARN_GOOGLE_AUTH_ENABLED on the backend)
    if (window.google?.accounts?.id) window.google.accounts.id.disableAutoSelect();
  } catch {
    /* non-fatal */
  }
  emitAuthChanged();
};

/* -------------------------------------------------------------- API ------ */

const request = async (path, options = {}) => {
  const response = await fetch(`${ULEARN_STUDENTS}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  // A 401 from a /me or other authenticated route means the stored session has
  // expired — clear it so the UI falls back to signed-out. A 401 from an
  // /auth/* route is the opposite: those are PRE-session (no token yet), so a
  // 401 there is a credential rejection (a wrong OTP code, a refused token) that
  // the caller must see with its `.status` intact — not a session to sign out.
  if (response.status === 401 && !path.startsWith("/auth/")) {
    signOutStudent();
    throw new Error("unauthenticated");
  }
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const error = new Error(`ulearn-students ${path} failed: ${response.status} ${text}`);
    // callers need the status to tell "your input was rejected" (4xx) apart
    // from "this service is down or misconfigured" (5xx), which need very
    // different words in front of a student
    error.status = response.status;
    throw error;
  }
  return response.json();
};

const emptyClaimed = { ielts: 0, major: 0, aptitude: 0 };

/**
 * Exchange a FIREBASE ID token (from a completed phone/OTP sign-in) for the
 * service's own JWT. On success the session is stored, historical attempts are
 * claimed server-side against the verified phone number, and stored onboarding
 * profiles gain the account's fields so existing test forms prefill unchanged.
 *
 * @param {string} idToken - firebase user.getIdToken()
 * @returns {Promise<{student: Object, claimed: {ielts: number, major: number, aptitude: number}}>}
 */
export const signInWithPhoneIdToken = async (idToken) => {
  const data = await request("/auth/phone", {
    method: "POST",
    body: JSON.stringify({ idToken }),
  });
  // Was this student already in the ulearn-students collection? Judged on the
  // record AS THE BACKEND RETURNED IT, before this device's fill-once blobs
  // are synced up (which would instantly make a brand-new record look old).
  // A `created` flag from the backend wins when present; otherwise a record
  // with none of the identity fields filled is taken to be freshly created.
  const preSync = data.student || {};
  const isNewStudent =
    typeof data.created === "boolean"
      ? data.created
      : !(hasValue(preSync.name) && hasValue(preSync.dob) && hasValue(preSync.nationality));
  storeSession(data.token, data.student);
  addSessionEvent("signed_in", { method: "phone" });
  // fill-once: anything this device already knows flows up to the account,
  // then the merged profile flows back into every form's prefill source
  await syncProfileFromTest(getKnownProfile());
  return {
    student: getStudent() || data.student,
    claimed: { ...emptyClaimed, ...(data.claimed || {}) },
    isNewStudent,
  };
};

/* ---------------------------------------------- SMS FALLBACK (Twilio) ----
 * Firebase's own SMS route refuses whole carriers — every STC Kuwait number
 * (+9655…) fails with "Error code: 39" while Zain/Ooredoo deliver fine, and
 * nothing in the Firebase console changes that. When Firebase refuses (or for a
 * prefix known to be refused), usePhoneOtp routes the SAME form through these
 * two calls, which the backend fulfils with Twilio Verify:
 *
 *   startFallbackOtp(phone)        POST /auth/phone/otp/start  → texts the code
 *   checkFallbackOtp(phone, code)  POST /auth/phone/otp/check  → Firebase custom token
 *
 * On an approved code the backend mints a Firebase CUSTOM token for the user
 * that owns the number; the caller signs into Firebase with it and then runs
 * the ordinary signInWithPhoneIdToken() exchange above — so the fallback
 * rejoins the native path and needs no special case downstream.
 *
 * Both reject with an Error whose `.status` carries the HTTP code, so the
 * caller can pick the right sentence: 400 (bad number), 401 (wrong/expired
 * code), 429 (rate limited), 503 (fallback not configured). The request()
 * 401-passthrough for /auth/* paths is what keeps a wrong-code 401 from being
 * mistaken for an expired session.
 * ------------------------------------------------------------------------- */

/**
 * Text a verification code to `e164` via the backend's Twilio Verify route.
 * @param {string} e164 - destination number in international format (+9655…)
 * @returns {Promise<{phone: string, status: string, channel: string}>}
 */
export const startFallbackOtp = (e164) =>
  request("/auth/phone/otp/start", {
    method: "POST",
    body: JSON.stringify({ phone: e164 }),
  });

/**
 * Confirm the fallback code. On success returns a Firebase CUSTOM TOKEN string
 * for the number's user; the caller passes it to signInWithCustomToken().
 * @param {string} e164 - the number the code was sent to
 * @param {string} code - the digits the student entered
 * @returns {Promise<string>} firebase custom token
 */
export const checkFallbackOtp = async (e164, code) => {
  const data = await request("/auth/phone/otp/check", {
    method: "POST",
    body: JSON.stringify({ phone: e164, code }),
  });
  return data.customToken;
};

/**
 * LEGACY Google exchange. The backend endpoint returns 503 unless
 * ULEARN_GOOGLE_AUTH_ENABLED is set, and no component calls this any more.
 * Kept so re-enabling Google is a two-line change rather than a rewrite.
 * @deprecated use signInWithPhoneIdToken
 */
export const signInWithGoogleIdToken = async (idToken) => {
  const data = await request("/auth/google", {
    method: "POST",
    body: JSON.stringify({ idToken }),
  });
  storeSession(data.token, data.student);
  addSessionEvent("signed_in", { email: data.student?.email });
  await syncProfileFromTest(getKnownProfile());
  return {
    student: getStudent() || data.student,
    claimed: { ...emptyClaimed, ...(data.claimed || {}) },
  };
};

export const fetchMe = async () => {
  const student = await request("/me");
  localStorage.setItem(STUDENT_KEY, JSON.stringify(student));
  return student;
};

/** @returns {Promise<{total: number, results: Array}>} unified history */
export const fetchMyTests = () => request("/me/tests");

/** @returns {Promise<Object>} per-type series, deltas, personal bests */
export const fetchMyProgress = () => request("/me/progress");

export const updateMyProfile = async (body) => {
  const student = await request("/me", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  localStorage.setItem(STUDENT_KEY, JSON.stringify(student));
  emitAuthChanged();
  return student;
};

/* ============================ FILL-ONCE PROFILE ==========================
 * One merged view of everything we already know about the student, so no
 * test form ever asks the same durable question twice.
 *
 * PRECEDENCE (highest first):
 *   1. the signed-in account profile (server truth, shared across devices)
 *   2. onboarding/results blobs already stored on THIS device
 *   3. the cached verifiedPhone
 * A field is "known" only when it resolves to a non-empty value.
 * ======================================================================== */

/** every durable field, i.e. a property of the STUDENT rather than of one attempt */
export const DURABLE_FIELDS = [
  'name',
  'email',
  'phone',
  'dob',
  'nationality',
  'destination',
  'studyLevel',
  'targetScore',
  'degree',
  'previousSchool',
  'cgpa',
  'englishProficiency',
];

const readBlob = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null') || {};
  } catch {
    return {};
  }
};

/** verifiedPhone is stored raw on some paths, JSON on others — normalise. */
const readVerifiedPhone = () => {
  try {
    const raw = localStorage.getItem('verifiedPhone');
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') return parsed;
      return parsed?.phone ? `${parsed.countryCode || ''}${parsed.phone}` : '';
    } catch {
      return raw;
    }
  } catch {
    return '';
  }
};

/** null/''/{}/[] all count as "not known" */
export const hasValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    return Object.values(value).some((v) => v !== null && v !== undefined && v !== '');
  }
  return true;
};

export const asValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const firstKnown = (...candidates) => {
  for (const candidate of candidates) {
    if (hasValue(candidate)) return candidate;
  }
  return null;
};

/** A value that is really just a phone number (some accounts were created with
 *  name === phone when the student verified a number without giving a name). */
const looksLikePhone = (s) => /^[+\d][\d\s()+-]{5,}$/.test(String(s || "").trim());

/**
 * The student's real display name, resolved consistently everywhere the account
 * is shown. It skips phone-shaped values (so an account whose stored name is a
 * phone number does not display the number as the name) and looks through every
 * onboarding blob for a genuine name. Returns "" when no real name exists — the
 * caller then decides the fallback (phone / generic).
 */
export const getDisplayName = () => {
  const student = getStudent() || {};
  const ielts = readBlob("ieltsUserProfile");
  const ieltsData = readBlob("ieltsUserData");
  const aptitude = readBlob("aptitudeTestData");
  const major = readBlob("majorUserData");
  const candidates = [
    student.name,
    ielts.name,
    ieltsData.name,
    aptitude.name,
    aptitude.fullName,
    major.fullName,
    major.name,
  ];
  for (const c of candidates) {
    const v = String(c || "").trim();
    if (v && !looksLikePhone(v)) return v;
  }
  return "";
};

/**
 * The merged profile every form prefills and skips from.
 * @returns {Object} durable fields; missing ones are null
 */
export const getKnownProfile = () => {
  const student = getStudent() || {};
  const ielts = readBlob('ieltsUserProfile');
  const ieltsData = readBlob('ieltsUserData');
  const aptitude = readBlob('aptitudeTestData');
  const major = readBlob('majorUserData');

  return {
    name: firstKnown(student.name, ielts.name, ieltsData.name, aptitude.name, major.fullName),
    email: firstKnown(student.email, ielts.email, ieltsData.email),
    phone: firstKnown(student.phone, ieltsData.phone, aptitude.phone, readVerifiedPhone()),
    dob: firstKnown(student.dob, ieltsData.dob, aptitude.dob),
    nationality: firstKnown(student.nationality, ielts.nationality, aptitude.nationality, major.nationality),
    destination: firstKnown(student.destination, ielts.destination, aptitude.destination, major.destination),
    studyLevel: firstKnown(student.studyLevel, ielts.studyLevel),
    targetScore: firstKnown(student.targetScore, ielts.targetScore),
    degree: firstKnown(student.degree, aptitude.degree, major.degree),
    previousSchool: firstKnown(student.previousSchool, major.previousSchool),
    cgpa: firstKnown(student.cgpa, major.cgpa),
    englishProficiency: firstKnown(student.englishProficiency, major.englishProficiency),
  };
};

/**
 * Is this durable field already known? Forms call this to decide whether to
 * ask. Only signed-in students get questions skipped: a signed-out student
 * sees every form exactly as before.
 * @param {Object} known - result of getKnownProfile()
 * @param {string} field
 */
export const isKnown = (known, field) => isSignedIn() && hasValue(known?.[field]);

/**
 * Push the account profile into the onboarding blobs the test flows read, so
 * a form that reads localStorage directly still sees current values.
 * Identity fields overwrite (the account is canonical once signed in);
 * everything else only fills gaps.
 */
export const prefillStoredProfiles = (student) => {
  if (!student) return;
  const apply = (key, overwrite, fillEmpty = {}) => {
    try {
      const blob = readBlob(key);
      let changed = false;
      Object.entries(overwrite).forEach(([field, value]) => {
        if (hasValue(value) && blob[field] !== value) {
          blob[field] = value;
          changed = true;
        }
      });
      Object.entries(fillEmpty).forEach(([field, value]) => {
        if (hasValue(value) && !hasValue(blob[field])) {
          blob[field] = value;
          changed = true;
        }
      });
      if (changed) localStorage.setItem(key, JSON.stringify(blob));
    } catch {
      /* malformed blob — leave it alone */
    }
  };
  const identity = {
    name: student.name,
    email: student.email,
    phone: student.phone,
    dob: student.dob,
  };
  const study = {
    nationality: student.nationality,
    destination: student.destination,
    studyLevel: student.studyLevel,
    targetScore: student.targetScore,
    degree: student.degree,
    previousSchool: student.previousSchool,
    cgpa: student.cgpa,
    englishProficiency: student.englishProficiency,
  };
  apply('ieltsUserProfile', identity, study);
  apply('ieltsUserData', identity, {});
  apply('aptitudeTestData', { name: student.name, phone: student.phone, dob: student.dob }, study);
  apply('majorUserData', { fullName: student.name }, study);
  if (hasValue(student.phone) && !readVerifiedPhone()) {
    try {
      localStorage.setItem('verifiedPhone', JSON.stringify({ phone: student.phone }));
    } catch {
      /* storage blocked */
    }
  }
};

/**
 * CAPTURE — called after a signed-in student submits any test. Every durable
 * field the flow collected is pushed to the account profile.
 *
 * FILL-EMPTY ONLY: a test form never overwrites a value the profile already
 * holds, so a deliberate Profile edit always wins. Fire-and-forget: a failed
 * sync can never break or delay a submission.
 * @param {Object} fields - durable fields collected in this flow
 */
export const syncProfileFromTest = async (fields) => {
  try {
    const student = getStudent();
    if (!student || !fields) return;
    const update = {};
    DURABLE_FIELDS.forEach((key) => {
      if (hasValue(fields[key]) && !hasValue(student[key])) update[key] = fields[key];
    });
    if (Object.keys(update).length) await updateMyProfile(update);
    prefillStoredProfiles(getStudent());
  } catch (error) {
    // profile sync must never break a test submission
    // eslint-disable-next-line no-console
    console.error('ulearn profile sync failed:', error);
  }
};

/**
 * OVERWRITE — for the test-specific questions a student answers on every
 * attempt (IELTS purpose/target, major GPA/school/English). Unlike
 * syncProfileFromTest, a fresh answer here REPLACES the stored one, so
 * "change it next time" actually sticks. Fire-and-forget.
 * @param {Object} fields - durable fields to replace on the account
 */
export const overwriteProfileFields = async (fields) => {
  try {
    if (!isSignedIn() || !fields) return;
    const update = {};
    DURABLE_FIELDS.forEach((key) => {
      if (hasValue(fields[key])) update[key] = fields[key];
    });
    if (Object.keys(update).length) await updateMyProfile(update);
    prefillStoredProfiles(getStudent());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("ulearn profile overwrite failed:", error);
  }
};

/* ------------------------------------------- Google Identity Services ---- */

let gsiPromise = null;

/** Load the GIS script once (no-op if already present). */
export const loadGoogleScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiPromise = null;
      reject(new Error("Failed to load Google sign-in"));
    };
    document.head.appendChild(script);
  });
  return gsiPromise;
};

/**
 * Render the official Google button into `container`.
 * @param {HTMLElement} container
 * @param {{onCredential: Function, locale?: string, width?: number}} opts
 * @returns {Promise<void>} rejects when GIS can't load or no client id is set
 */
export const renderGoogleButton = async (container, opts) => {
  if (!GOOGLE_CLIENT_ID) throw new Error("missing-client-id");
  await loadGoogleScript();
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => opts.onCredential(response.credential),
    auto_select: false,
    cancel_on_tap_outside: true,
  });
  window.google.accounts.id.renderButton(container, {
    type: "standard",
    theme: "outline",
    size: "large",
    shape: "pill",
    text: "signin_with",
    logo_alignment: "left",
    locale: opts.locale === "ar" ? "ar" : "en",
    width: opts.width || 280,
  });
};
